import { FormEvent, useState } from 'react';
import dayjs from 'dayjs';
import { useTexts } from 'utils/useTexts';
import { useFormContext } from '@context/FormContext';
import CustomSelect from '@components/shared/form/CustomSelectInput';
import { Button } from '@components/shared/Button/Button';
import { incomeTypes, otherIncomeSources } from '@shared/constants/incomeType';
import useForm from '@shared/hooks/useForm';
import { CDX_ACTIONS, IEmploymentForm, IIncomeForm, INonEmploymentForm } from 'types/form';
import { AssetLinks } from '@assets/assets';
import { TextInput } from '@components/shared/form/TextInput';
import { PriceInput } from '@components/shared/form/PriceTextInput';
import { countryStates } from '@shared/constants/countryStates';
import { CheckInput } from '@components/shared/form/CheckInput';
import { PhoneTextInput } from '@components/shared/form/PhoneTextInput';
import { Alert } from '@components/shared/Alert/Alert';
import {
  IEmploymentDetailsPayload,
  IOtherIncomeDetailsPayload,
  updateEmploymentDetails,
  updateOtherIncomeDetails,
} from '@services/employmentAndIncomeDetails';
import { getYearsAndMonthsBetween, monthsBetween, todaysDate } from 'utils/dateUtils';
import { initialEmploymentFormData, initialIncomeFormData, nonEmploymentInitialFormData } from '@context/formReducer';
import { createErrorResponse, REQUIRED_MONTHS } from '@services/utils';
import useEffectOnce from '@shared/hooks/useEffectOnce';
import { useAnalytics } from '@shared/hooks/useAnalytics';
import { SEGMENT_SCREEN_PARAMS } from '@shared/constants/segmentConstants';
import { setLocalStorage } from 'utils/setLocalStorage';
import { convertArrayToSnakeCase } from 'utils/stringFormat';
import { useScreenNavigator } from '@shared/hooks/useScreenNavigator';
import AdditionalIncomeConfirmationModal from './AdditionalIncomeConfirmationModal';
import AddMoreAdditionalIncomeModal from './AddMoreAdditionalIncomeModal';
import {
  addOrEditEmployment,
  addOrEditOtherIncome,
  EMPLOYMENT_TYPES,
  employmentFormValidation,
  formatEmploymentDetails,
  formatOtherIncomeDetails,
  incomeFormValidation,
  NON_EMPLOYMENT_TYPES,
  nonEmploymentValidation,
  OTHER_EMPLOYMENT,
} from './employmentUtils';

const EmploymentAndIncomeForm = () => {
  const { t } = useTexts();
  const { next } = useScreenNavigator();
  const { trackClick, trackFormSubmit, trackPage, startInteraction } = useAnalytics(SEGMENT_SCREEN_PARAMS.EMPLOYMENT_LOOP);
  const {
    currentStep,
    employmentFormData,
    incomeFormData,
    isLoading,
    submitStart,
    submitSuccess,
    dispatch,
    updateEmploymentForm,
    updateIncomeForm,
  } = useFormContext();
  const [incomeType, setIncomeType] = useState('');
  const [otherIncomeSource, setOtherIncomeSource] = useState('');
  const [employmentDetailsData, setEmploymentDetailsData] = useState<IEmploymentDetailsPayload[]>([]);
  const [otherIncomeDetails, setOtherIncomeDetails] = useState<IOtherIncomeDetailsPayload[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [incomeEditingIndex, setIncomeEditingIndex] = useState<number | null>(null);
  const [noOfMonths, setNoOfMonths] = useState<number>(0);
  const [totalMonthsSubmitted, setTotalMonthsSubmitted] = useState<number>(0);
  const [showDuplicateEmploymentAlert, setShowDuplicateEmploymentAlert] = useState(false);
  const [displayAdditionalIncomeModal, setDisplayAdditionalIncomeModal] = useState(false);
  const [displayMoreAdditionalIncomeModal, setDisplayMoreAdditionalIncomeModal] = useState(false);
  const [addMoreIncome, setAddMoreIncome] = useState(false);

  const isEmploymentType = EMPLOYMENT_TYPES.includes(incomeType ?? '');
  const isNonEmploymentType = NON_EMPLOYMENT_TYPES.includes(incomeType ?? '');

  const validationSchema = isEmploymentType
    ? employmentFormValidation
    : isNonEmploymentType
    ? nonEmploymentValidation
    : incomeFormValidation;
  const initialValues = isEmploymentType
    ? initialEmploymentFormData
    : isNonEmploymentType
    ? nonEmploymentInitialFormData
    : initialIncomeFormData;

  useEffectOnce(() => trackPage(t('pageTitles.EmploymentAndIncome')));

  const totalMonthsAtJobCombined = (
    employmentDetails: IEmploymentDetailsPayload[],
    otherIncomeDetails: IOtherIncomeDetailsPayload[]
  ) => {
    const employmentDates = [...employmentDetails, ...otherIncomeDetails];
    const formatDateRange = employmentDates.map((employment) => {
      return { from: employment.startDate, to: employment.endDate === '' ? dayjs() : employment.endDate };
    });

    const allMonths = new Set<string>();

    formatDateRange.forEach((range) => {
      const fromDate = dayjs(range.from);
      const toDate = dayjs(range.to);

      // Get the first month and iterate through each month in the range
      let currentMonth = fromDate.startOf('month');

      while (currentMonth.isBefore(toDate) || currentMonth.isSame(toDate, 'month')) {
        // Calculate the actual start and end dates

        const monthStart = fromDate.isAfter(currentMonth) ? fromDate : currentMonth;
        const monthEnd = toDate.isBefore(currentMonth.endOf('month')) ? toDate : currentMonth.endOf('month');

        // Use dayjs diff to calculate days in this month
        const daysInMonth = monthEnd.diff(monthStart, 'day') + 1;

        // Get the total days in this calendar month
        const totalDaysInMonth = currentMonth.daysInMonth();

        // Count as full month if
        // 30+ days
        //complete month (all days of that month)
        const isFullMonth = daysInMonth >= 30 || daysInMonth === totalDaysInMonth;

        if (isFullMonth) {
          const monthKey = currentMonth.format('YYYY-MM');
          allMonths.add(monthKey);
        }

        // Move to next month
        currentMonth = currentMonth.add(1, 'month');
      }
    });
    return allMonths.size;
  };

  const isDuplicateEmployment = (newEmployment: IEmploymentForm): boolean => {
    return employmentDetailsData.some(
      (employment) =>
        employment.jobTitle === newEmployment.jobTitle &&
        employment.companyName === newEmployment.employersName &&
        employment.city === newEmployment.employersCity &&
        employment.state === newEmployment.employersState &&
        employment.zip === newEmployment.employersZipCode &&
        employment.streetLine1 === newEmployment.employersAddress &&
        employment.startDate === newEmployment.startDate
    );
  };

  const isDuplicateIncome = (newIncome: IIncomeForm): boolean => {
    return otherIncomeDetails.some((otherIncome) => otherIncome.incomeType === newIncome.otherIncomeSource);
  };

  const resetFormValues = () => {
    resetForm();
    setIncomeType('');
    setOtherIncomeSource('');
    updateEmploymentForm(initialEmploymentFormData);
    updateIncomeForm(initialIncomeFormData);
  };

  const handleEmploymentSubmit = async (values: IEmploymentForm) => {
    let updatedEmploymentDetails = [...employmentDetailsData];

    //Updated Other Income
    const updatedOtherIncomeDetails = [...otherIncomeDetails];

    if (editingIndex !== null) {
      // Replace the existing item
      updatedEmploymentDetails[editingIndex] = formatEmploymentDetails(values);
    } else {
      // Append a new item
      updatedEmploymentDetails = [...updatedEmploymentDetails, formatEmploymentDetails(values)];
    }

    if (incomeEditingIndex !== null) {
      //on edit of other income details if income type is changed to employment type then remove that data from other income list and add a new entry to employment list

      updatedOtherIncomeDetails.splice(incomeEditingIndex, 1);
      setOtherIncomeDetails(updatedOtherIncomeDetails);
      setIncomeEditingIndex(null);
    }

    if (editingIndex !== null) {
      // Editing existing employment
      setEmploymentDetailsData(updatedEmploymentDetails);
      setEditingIndex(null);
      setShowDuplicateEmploymentAlert(false);
    } else {
      // Adding new employment
      if (!isDuplicateEmployment(values)) {
        setEmploymentDetailsData(updatedEmploymentDetails);
        setShowDuplicateEmploymentAlert(false);
      } else {
        setShowDuplicateEmploymentAlert(true);
        return;
      }
    }
    const currentMonthsSubmitted = totalMonthsAtJobCombined(updatedEmploymentDetails, updatedOtherIncomeDetails);
    setTotalMonthsSubmitted(currentMonthsSubmitted);
    const remainingMonths = Math.max(0, REQUIRED_MONTHS - currentMonthsSubmitted);
    setNoOfMonths(remainingMonths);
    const currentEmploymentTotal = [...updatedEmploymentDetails, ...updatedOtherIncomeDetails];
    const trackSubmitObject = {
      income_sources_provided_count: currentEmploymentTotal.length,
      months_of_history_captured: currentMonthsSubmitted,
      months_of_history_required: REQUIRED_MONTHS,
      months_of_history_remaining: remainingMonths,
    };

    trackFormSubmit('Form Submitted', {
      submission_status: true,
      submission_method: 'manual',
      ...trackSubmitObject,
    });

 
    if (currentMonthsSubmitted >= REQUIRED_MONTHS) {
      if (addMoreIncome) {
        setDisplayMoreAdditionalIncomeModal(true);
        trackClick('Prompt Shown', {
          prompt_id: 'additional_income_prompt',
          prompt_type: 'modal',
          ...trackSubmitObject,
        });
      } else {
        setDisplayAdditionalIncomeModal(true);
      }
    } else {
      setDisplayAdditionalIncomeModal(false);
      setDisplayMoreAdditionalIncomeModal(false);
      setAddMoreIncome(false);
      resetFormValues();
    }
  };

  const handleOtherIncomeSubmit = (values: IIncomeForm) => {
    const updatedOtherIncomeDetails = [...otherIncomeDetails];
    const latestIncomeDetails = formatOtherIncomeDetails(values);

    //Updated Employment
    const updatedEmploymentDetails = [...employmentDetailsData];

    if (editingIndex !== null) {
      //on edit of employment details if income type is changed to other then remove that data from employment list and add a new entry to other income list
      updatedEmploymentDetails.splice(editingIndex, 1);
      setEmploymentDetailsData(updatedEmploymentDetails);

      setEditingIndex(null);
    }

    if (incomeEditingIndex !== null) {
      updatedOtherIncomeDetails[incomeEditingIndex] = latestIncomeDetails;
      setOtherIncomeDetails(updatedOtherIncomeDetails);
      setIncomeEditingIndex(null);
      setShowDuplicateEmploymentAlert(false);
      resetFormValues();
    } else {
      // Adding new other income
      if (!isDuplicateIncome(values)) {
        if (noOfMonths === 0 && addMoreIncome) {
          setDisplayMoreAdditionalIncomeModal(true);
        }
        setShowDuplicateEmploymentAlert(false);

        setOtherIncomeDetails([...otherIncomeDetails, latestIncomeDetails]);
        resetFormValues();
      } else {
        setShowDuplicateEmploymentAlert(true);
        return;
      }
    }
    const currentMonthsSubmitted = totalMonthsAtJobCombined(updatedEmploymentDetails, [
      ...otherIncomeDetails,
      latestIncomeDetails,
    ]);
    setTotalMonthsSubmitted(currentMonthsSubmitted);
    const remainingMonths = Math.max(0, REQUIRED_MONTHS - currentMonthsSubmitted);
    setNoOfMonths(remainingMonths);


    const currentEmploymentTotal = [...updatedEmploymentDetails,  [
      ...otherIncomeDetails,
      latestIncomeDetails,
    ]];
    const trackSubmitObject = {
      income_sources_provided_count: currentEmploymentTotal.length,
      months_of_history_captured: currentMonthsSubmitted,
      months_of_history_required: REQUIRED_MONTHS,
      months_of_history_remaining: remainingMonths,
    };

    trackFormSubmit('Form Submitted', {
      submission_status: true,
      submission_method: 'manual',
      ...trackSubmitObject,
    });
    

    if (currentMonthsSubmitted >= REQUIRED_MONTHS) {
      if (addMoreIncome) {
        setDisplayMoreAdditionalIncomeModal(true);
      } else {
        setDisplayAdditionalIncomeModal(true);
      }
    } else {
      setDisplayAdditionalIncomeModal(false);
      setDisplayMoreAdditionalIncomeModal(false);
      setAddMoreIncome(false);
      resetFormValues();
    }
  };

  const { errors, setValues, resetForm, handleChange, handleSubmit } = useForm<
    IEmploymentForm | IIncomeForm | INonEmploymentForm
  >({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      if (isEmploymentType) {
        handleEmploymentSubmit(values as IEmploymentForm);
      } else if (incomeType === OTHER_EMPLOYMENT) {
        handleOtherIncomeSubmit(values as IIncomeForm);
      } else {
        next(currentStep, 'postSubmitOutcome', { }, true);
      }
    },
    onValidationError: (errors, values) => {
      let currentValue = null;
      if (isEmploymentType) {
        currentValue = values as IEmploymentForm;
      }
      if (incomeType === OTHER_EMPLOYMENT) {
        currentValue = values as IIncomeForm;
      }
      const monthsBetweenTotal = currentValue ? monthsBetween(currentValue?.startDate, new Date()) : 0;
      trackFormSubmit('Form Submitted', {
        submission_status: false,
        submission_method: 'manual',
        income_sources_provided_count: employmentDetailsData.length + 1,
        submission_fail_reason: 'validation_error',
        validation_errors: convertArrayToSnakeCase(Object.keys(errors)),
        months_of_history_captured: Math.abs(noOfMonths ?? 0 + monthsBetweenTotal),
        months_of_history_required: REQUIRED_MONTHS,
        months_of_history_remaining: Math.abs(REQUIRED_MONTHS - monthsBetweenTotal),
      });
    },
  });

  const formatYearsAndMonths = (start: string, end?: string, isCurrent?: boolean): string => {
    const startDate = dayjs(start);
    const endDate = isCurrent
      ? dayjs() // Current date
      : dayjs(end || dayjs().format('YYYY-MM-DD'));

    const { years, months } = getYearsAndMonthsBetween(startDate.toDate(), endDate.toDate());
    return `${years} ${years === 1 ? 'Year' : 'Years'} & ${months} ${months === 1 ? 'Month' : 'Months'}`;
  };
  const isOtherIncomeType = incomeType === OTHER_EMPLOYMENT;
  const employmentErrors: Partial<Record<keyof IEmploymentForm, string>> = isEmploymentType
    ? (errors as Record<keyof IEmploymentForm, string>)
    : {};
  const incomeErrors: Partial<Record<keyof IIncomeForm, string>> = isOtherIncomeType
    ? (errors as Record<keyof IIncomeForm, string>)
    : {};

  const handleInputChange = <K extends keyof (IEmploymentForm & IIncomeForm)>(
    field: K,
    value: (IEmploymentForm & IIncomeForm)[K]
  ) => {
    //  eslint-disable-next-line @typescript-eslint/no-explicit-any
    (handleChange as any)(field, value);
    if (isEmploymentType) {
      updateEmploymentForm({ [field]: value });
    } else {
      updateIncomeForm({ [field]: value });
    }
  };

  const handleEmploymentEdit = (index: number, editingEmployment: IEmploymentForm) => {
    setEditingIndex(index);
    setIncomeEditingIndex(null);
    setIncomeType(editingEmployment.incomeType);
    (Object.keys(editingEmployment) as (keyof IEmploymentForm)[]).forEach((key) => {
      const value = editingEmployment[key];
      handleInputChange(key, value);
    });
    setValues(editingEmployment);
    updateEmploymentForm(editingEmployment);
  };

  const handleOtherIncomeEdit = (index: number, editingOtherIncome: IIncomeForm) => {
    setIncomeEditingIndex(index);
    setEditingIndex(null);
    const selectedIncomeType = OTHER_EMPLOYMENT;
    setIncomeType(selectedIncomeType);
    setOtherIncomeSource(editingOtherIncome.otherIncomeSource);
    (Object.keys(editingOtherIncome) as (keyof IIncomeForm)[]).forEach((key) => {
      const value = editingOtherIncome[key];
      handleInputChange(key, value);
    });
    setValues(editingOtherIncome);
    updateIncomeForm(editingOtherIncome);
  };

  const closeAdditionalIncomeModal = () => {
    setDisplayAdditionalIncomeModal(false);
    setDisplayMoreAdditionalIncomeModal(false);
  };

  const handleAddMore = () => {
    trackClick('CTA Clicked', {
      cta_id: 'additional_income_add_more',
      cta_text: 'Add More',
      cta_type: 'secondary_button',
      cta_context: 'additional_income_prompt',
    });
    resetFormValues();
    setAddMoreIncome(true);
    setDisplayAdditionalIncomeModal(false);
    setDisplayMoreAdditionalIncomeModal(false);
  };

  const handleNoImDone = () => {
    trackClick('CTA Clicked', {
      cta_id: 'additional_income_no_more',
      cta_text: 'No, I\'m Done',
      cta_type: 'secondary_button',
      cta_context: 'additional_income_prompt',
    });
    setAddMoreIncome(false);
    setDisplayAdditionalIncomeModal(false);
    setDisplayMoreAdditionalIncomeModal(false);
    submitEmploymentAndIncomeDetails();
  };

  const submitEmploymentAndIncomeDetails = async () => {
    submitStart();
    try {
      const updateEmploymentResponse = await updateEmploymentDetails(employmentDetailsData);

      if ('error' in updateEmploymentResponse) {
        dispatch({
          type: CDX_ACTIONS.SUBMIT_API_ERROR,
          payload: updateEmploymentResponse,
        });
      } else {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const trackingFormatEmploymentDetailsData = employmentDetailsData.map(({ monthlyIncomeAmount, ...rest }) => ({
          ...rest,
          months_at_employer: rest?.monthsAtJob ?? 0,
        }));
        trackFormSubmit('Employment Information Provided', {
          ...trackingFormatEmploymentDetailsData,
          qualified_status: true,
          completed_status: true,
          submission_method: 'manual',
          employment_status: incomeType,
          total_months_provided: noOfMonths,
        });
        const updateOtherIncomeResponse = await updateOtherIncomeDetails(otherIncomeDetails);

        if ('error' in updateOtherIncomeResponse) {
          dispatch({
            type: CDX_ACTIONS.SUBMIT_API_ERROR,
            payload: updateOtherIncomeResponse,
          });
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const trackingFormatEmploymentDetailsData = otherIncomeDetails.map(({ ...rest }) => ({
            ...rest,
            months_at_employer: rest?.monthsAtJob ?? 0,
          }));
          trackFormSubmit('Employment Information Provided', {
            ...trackingFormatEmploymentDetailsData,
            qualified_status: true,
            completed_status: true,
            submission_method: 'manual',
            employment_status: incomeType,
            total_months_provided: noOfMonths,
          });
          setLocalStorage(true, 'employmentAndIncomeAdded');
          submitSuccess();
          next(currentStep, 'postSubmitOutcome', { }, true);
        }
      }
    } catch (err) {
      dispatch({
        type: CDX_ACTIONS.SUBMIT_API_ERROR,
        payload: createErrorResponse(
          err instanceof Error ? err.name : 400,
          err instanceof Error ? err.message : 'Error while updating employment details and other income '
        ),
      });
    }
  };

  const handleAnswerLater = () => {
    trackClick('CTA Clicked', {
      cta_id: 'employment_answer_later',
      cta_text: 'Answer Later',
      cta_type: 'link',
      cta_context: 'employment_loop',
    });

    setLocalStorage(false, 'employmentAndIncomeAdded');
    next(currentStep, 'postSubmitOutcome', { }, true);
  };

  const handleIncomeTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    resetFormValues();
    const selectedIncomeType = e.target.value;
    setIncomeType(selectedIncomeType);
    updateEmploymentForm({ ...initialEmploymentFormData, incomeType: selectedIncomeType });
    updateIncomeForm({ ...initialIncomeFormData, incomeType: selectedIncomeType });
    handleInputChange('incomeType', selectedIncomeType);
  };

  function isEmploymentForm(data: IEmploymentForm | INonEmploymentForm): data is IEmploymentForm {
    return EMPLOYMENT_TYPES.includes(data.incomeType);
  }

  const handleFormSubmision = (e: FormEvent) => {
    e.preventDefault();
    totalMonthsSubmitted >= 24 && addMoreIncome && incomeType === '' ? handleNoImDone() : handleSubmit();
  };

  return (
    <div className="iLendingCDX-flex-column">
      {employmentDetailsData.length >= 1 ? (
        <h1 className="iLendingCDX-form-title">{t('incomeSources.title')}</h1>
      ) : (
        <>
          <h1 className="iLendingCDX-form-title">{t('employmentAndIncome.title')}</h1>
          <h1 className="iLendingCDX-form-title--small iLendingCDX-mb-4">{t('employmentAndIncome.title2')}</h1>
        </>
      )}
      {employmentDetailsData.length >= 1 && (
        <>
          {employmentDetailsData.map((employment, index) => (
            <div
              key={`${employment.jobTitle}${employment.companyName}${employment.startDate}${employment.employmentType}`}
              className="iLendingCDX-verifyInfoCard iLendingCDX-mb-4"
            >
              <div className="iLendingCDX-verifyInfoCard-Icon ">
                <img
                  src={AssetLinks.employment}
                  alt=""
                />
              </div>

              <div className="iLendingCDX-verifyInfoCard-info">
                <p>{employment.companyName}</p>
                <p className="iLendingCDX-verifyInfoCard-info--small">
                  {formatYearsAndMonths(employment.startDate, employment.endDate, employment.isCurrent)}
                </p>
              </div>

              <img
                className="iLendingCDX-edit-employment"
                src={AssetLinks.pencil}
                alt="pencil-icon"
                onClick={() => {
                  handleEmploymentEdit(index, {
                    ...addOrEditEmployment(employment),
                  });
                }}
              />
            </div>
          ))}
        </>
      )}

      {otherIncomeDetails.length >= 1 && (
        <>
          {otherIncomeDetails.map((otherIncome, index) => (
            <div
              key={`${otherIncome.incomeType}${otherIncome.monthlyAmount}${otherIncome.startDate}${otherIncome.incomeType}`}
              className="iLendingCDX-verifyInfoCard iLendingCDX-mb-4"
            >
              <div className="iLendingCDX-verifyInfoCard-Icon ">
                <img
                  src={AssetLinks.employment}
                  alt=""
                />
              </div>
              <div className="iLendingCDX-verifyInfoCard-info">
                <p>{otherIncome.incomeType}</p>
                <p className="iLendingCDX-verifyInfoCard-info--small">
                  {formatYearsAndMonths(otherIncome.startDate ?? '', otherIncome.endDate, otherIncome.isCurrent)}
                </p>
              </div>

              <img
                className="iLendingCDX-edit-employment"
                src={AssetLinks.pencil}
                alt=""
                onClick={() => {
                  handleOtherIncomeEdit(index, {
                    ...addOrEditOtherIncome(otherIncome),
                  });
                }}
              />
            </div>
          ))}
        </>
      )}
      <>
        {(employmentDetailsData.length >= 1 || otherIncomeDetails.length >= 1) &&
          (addMoreIncome ? (
            <h1 className="iLendingCDX-form-title2">{t('incomeSources.addAdditionalIncome')}</h1>
          ) : (
            <>
              <h1 className="iLendingCDX-form-title2">{t('incomeSources.title2')}</h1>
              <p className="iLendingCDX-remaining-months iLendingCDX-mb-4">
                {t('incomeSources.validation.justNeed')}
                <b>{noOfMonths}</b>
                {t('incomeSources.validation.moreMonths')}
              </p>
            </>
          ))}
      </>
      {showDuplicateEmploymentAlert && (
        <div className={'iLendingCDX-mb-4'}>
          <Alert message={t('employmentAndIncome.duplicateValidation')} />
        </div>
      )}
      <form
        onSubmit={handleFormSubmision}
        onFocus={startInteraction}
      >
        <div className="employmentAndIncome__container">
          <CustomSelect
            label={t('employmentAndIncome.incomeType')}
            className={'iLendingCDX-mb-4'}
            options={incomeTypes}
            placeholder={t('employmentAndIncome.incomeType')}
            value={incomeType}
            onChange={(e) => handleIncomeTypeChange(e)}
          />
          {isEmploymentForm(employmentFormData) && EMPLOYMENT_TYPES.includes(incomeType ?? '') && (
            <>
              <TextInput
                label={t('employmentAndIncome.employmentForm.employersName')}
                className={'iLendingCDX-mb-2'}
                name="employersName"
                value={employmentFormData.employersName ?? ''}
                errorMessage={employmentErrors.employersName}
                onChange={(e) => handleInputChange('employersName', e.target.value)}
              />
              <TextInput
                label={t('employmentAndIncome.employmentForm.jobTitle')}
                className={'iLendingCDX-mb-2'}
                name="jobTitle"
                value={employmentFormData.jobTitle ?? ''}
                errorMessage={employmentErrors.jobTitle}
                onChange={(e) => handleInputChange('jobTitle', e.target.value)}
              />
              <TextInput
                label={t('employmentAndIncome.employmentForm.employersAddress')}
                className={'iLendingCDX-mb-2'}
                name="employersAddress"
                value={employmentFormData.employersAddress ?? ''}
                errorMessage={employmentErrors.employersAddress}
                onChange={(e) => handleInputChange('employersAddress', e.target.value)}
              />
              <TextInput
                label={t('employmentAndIncome.employmentForm.employersAPTSuiteNumber')}
                className={'iLendingCDX-mb-2'}
                name="employersAPTSuiteNumber"
                value={employmentFormData.employersAPTSuiteNumber ?? ''}
                errorMessage={employmentErrors.employersAPTSuiteNumber}
                onChange={(e) => handleInputChange('employersAPTSuiteNumber', e.target.value)}
              />
              <TextInput
                label={t('employmentAndIncome.employmentForm.employersZipCode')}
                className={'iLendingCDX-mb-2'}
                name="employersZipCode"
                value={employmentFormData.employersZipCode || ''}
                errorMessage={employmentErrors.employersZipCode}
                onChange={(e) => handleInputChange('employersZipCode', e.target.value)}
              />
              <TextInput
                label={t('employmentAndIncome.employmentForm.employersCity')}
                className={'iLendingCDX-mb-2'}
                name="employersCity"
                value={employmentFormData.employersCity ?? ''}
                errorMessage={employmentErrors.employersCity}
                onChange={(e) => handleInputChange('employersCity', e.target.value)}
              />

              <CustomSelect
                label={t('employmentAndIncome.employmentForm.employersState')}
                className={'iLendingCDX-mb-4'}
                name="employersState"
                options={countryStates}
                value={employmentFormData.employersState ?? ''}
                errorMessage={employmentErrors.employersState}
                onChange={(e) => handleInputChange('employersState', e.target.value)}
              />
              <PhoneTextInput
                label={t('employmentAndIncome.employmentForm.employersPhoneNumber')}
                className={'iLendingCDX-mb-4'}
                name="employersPhoneNumber"
                value={employmentFormData.employersPhoneNumber ?? ''}
                errorMessage={employmentErrors.employersPhoneNumber}
                onChange={(value) => handleInputChange('employersPhoneNumber', value as IEmploymentForm['employersPhoneNumber'])}
              />

              <TextInput
                label={t('employmentAndIncome.employmentForm.startDate')}
                className={'iLendingCDX-mb-4'}
                name="startDate"
                type="date"
                autoComplete="off"
                value={employmentFormData.startDate ?? ''}
                errorMessage={employmentErrors.startDate}
                max={todaysDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
              />
              <CheckInput
                className={'iLendingCDX-mb-8 iLendingCDX--check'}
                label={t('employmentAndIncome.employmentForm.currentSourceOfIncome')}
                checked={employmentFormData.isCurrentIncomeSource}
                onChange={(e) => {
                  handleInputChange('endDate', '');
                  handleInputChange('isCurrentIncomeSource', e.target.checked);
                }}
              />

              {!employmentFormData.isCurrentIncomeSource && (
                <TextInput
                  label={t('employmentAndIncome.employmentForm.endDate')}
                  className={'iLendingCDX-mb-4'}
                  name="endDate"
                  type="date"
                  autoComplete="off"
                  value={employmentFormData.endDate ?? ''}
                  errorMessage={employmentErrors.endDate}
                  max={todaysDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                />
              )}

              <PriceInput
                label={t('employmentAndIncome.employmentForm.annualIncome')}
                className={'iLendingCDX-mb-8'}
                name="annualIncome"
                value={employmentFormData.annualIncome ?? 0}
                errorMessage={employmentErrors.annualIncome}
                onChange={(value) => handleInputChange('annualIncome', value as IEmploymentForm['annualIncome'])}
              />
            </>
          )}

          {incomeType === 'Other' && !['Student', 'Unemployed', 'Self Employed', ''].includes(incomeType) && (
            <>
              <CustomSelect
                label={t('employmentAndIncome.incomeForm.otherIncomeSource')}
                className={'iLendingCDX-mb-4'}
                name={t('employmentAndIncome.incomeForm.otherIncomeSource')}
                options={otherIncomeSources}
                placeholder={t('employmentAndIncome.incomeForm.otherIncomeSource')}
                value={incomeFormData.otherIncomeSource}
                onChange={(e) => {
                  handleInputChange('otherIncomeSource', e.target.value);
                  setOtherIncomeSource(e.target.value);
                }}
              />
              {otherIncomeSource !== '' && (
                <>
                  <TextInput
                    label={t('employmentAndIncome.incomeForm.startDate')}
                    className={'iLendingCDX-mb-4'}
                    name="startDate"
                    type="date"
                    autoComplete="off"
                    value={incomeFormData.startDate ?? ''}
                    errorMessage={incomeErrors.startDate}
                    max={todaysDate}
                    onChange={(e) => handleInputChange('startDate', e.target.value)}
                  />
                  <CheckInput
                    className={'iLendingCDX-mb-8 iLendingCDX--check'}
                    label={t('employmentAndIncome.incomeForm.currentSourceOfIncome')}
                    checked={incomeFormData.isCurrentIncomeSource}
                    onChange={(e) => {
                      handleInputChange('endDate', '');
                      handleInputChange('isCurrentIncomeSource', e.target.checked);
                    }}
                  />

                  {!incomeFormData.isCurrentIncomeSource && (
                    <TextInput
                      label={t('employmentAndIncome.incomeForm.endDate')}
                      className={'iLendingCDX-mb-4'}
                      name="endDate"
                      type="date"
                      autoComplete="off"
                      value={incomeFormData.endDate ?? ''}
                      errorMessage={incomeErrors.endDate}
                      max={todaysDate}
                      onChange={(e) => handleInputChange('endDate', e.target.value)}
                    />
                  )}

                  <PriceInput
                    label={t('employmentAndIncome.incomeForm.monthlyAmount')}
                    className={'iLendingCDX-mb-8'}
                    name="monthlyAmount"
                    value={incomeFormData.monthlyAmount}
                    errorMessage={incomeErrors.monthlyAmount}
                    onChange={(value) => handleInputChange('monthlyAmount', value as IIncomeForm['monthlyAmount'])}
                  />
                </>
              )}
            </>
          )}
        </div>
        <Button
          variant="ghost"
          size="lg"
          className={'iLendingCDX-mb-2'}
          onClick={handleAnswerLater}
        >
          {t('employmentAndIncome.answerLater')}{' '}
        </Button>
        <Button
          type="submit"
          className="iLendingCDX-button--rounded "
          size="lg"
          isLoading={isLoading}
        >
          {t('employmentAndIncome.continue')}
        </Button>
      </form>
      {displayAdditionalIncomeModal && (
        <AdditionalIncomeConfirmationModal
          isModalOpen={displayAdditionalIncomeModal}
          closeModal={closeAdditionalIncomeModal}
          handleAddMore={handleAddMore}
          handleNoImDone={handleNoImDone}
        />
      )}
      {displayMoreAdditionalIncomeModal && (
        <AddMoreAdditionalIncomeModal
          isModalOpen={displayMoreAdditionalIncomeModal}
          closeModal={closeAdditionalIncomeModal}
          handleYesIDo={handleAddMore}
          handleNoImDone={handleNoImDone}
        />
      )}
    </div>
  );
};
export default EmploymentAndIncomeForm;
